import {useEffect, useMemo, useRef} from 'preact/hooks';

import {PAY_AUTH_DOMAIN} from '~/constants/authorize';
import {useRootProvider} from '~/foundation/RootProvider/hooks';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import register from '~/utils/register';
import {isoWindow} from '~/utils/window';

import type {ShopCartSyncProps, ShopCartSyncWebComponentProps} from './types';
import {useShopCartSyncEventListener} from './utils';

export const ShopCartSyncInternal = (_props: ShopCartSyncProps) => {
  const {instanceId} = useRootProvider();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {destroy} = useShopCartSyncEventListener({
    source: iframeRef,
  });

  useEffect(() => {
    const iframe = iframeRef.current;
    return () => {
      if (iframe) {
        destroy();
      }
    };
  }, [destroy]);

  const url = useMemo(() => {
    const params = new URLSearchParams({
      /* eslint-disable @typescript-eslint/naming-convention */
      analytics_trace_id: instanceId,
      target_origin: isoWindow.location.origin,
      /* eslint-enable @typescript-eslint/naming-convention */
    } as Record<string, string>);

    const path = '/pay/session/hop';

    return `${PAY_AUTH_DOMAIN}${path}?${params}`;
  }, [instanceId]);

  return (
    <iframe
      className="hidden"
      data-testid="shop-cart-sync-iframe"
      ref={iframeRef}
      src={url}
    />
  );
};

register<ShopCartSyncProps>(
  ({element, ...props}) => (
    <RootProvider element={element} featureName="ShopCartSync">
      <ShopCartSyncInternal {...props} />
    </RootProvider>
  ),
  {
    name: 'shop-cart-sync',
    shadow: 'open',
  },
);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shop-cart-sync']: ShopCartSyncWebComponentProps;
    }
  }
}

export function ShopCartSync(props: ShopCartSyncWebComponentProps) {
  return <shop-cart-sync {...props} />;
}
